// SegmentPersonaZone — who we want to meet, and the search that finds more of them (§10, §13).
//
// A segment persona is the OPERATOR's own form: titles, seniorities, departments, sectors,
// cantons, topics. It is written held on the spot and carries no claim mark, because the
// operator is its author and it asserts nothing about anybody — it describes an intention.
//
// "Find lookalikes" runs it through Surfe and files `candidate` proposals. The daily cap is
// shown BEFORE the click and again in the answer: a cap that was reached is a fact the operator
// must see, not a shorter list they are left to explain to themselves.
import { useCallback, useEffect, useState } from 'react';
import { Att, Fig, Lnk, Sep, Sheet, Tag, Zone, ZoneHead } from './PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { ROUTES, open } from '../routes.js';
import { createSegmentPersona, listPersonas, lookalikes, type LookalikeResult } from '../api/personas.js';
import { ApiError } from '../api/client.js';
import { useToast } from './Toast.js';
import './SegmentPersonaZone.de.js';
import './SegmentPersonaZone.fr.js';
import type { Persona, SegmentPersonaInput, Trait } from '../../shared/types.js';

/** No required props: the zone is one instrument on the Campaigns page and reads its own rows. */
export interface SegmentPersonaZoneProps {
  /** Called after a segment is written or a lookalike run files candidates. */
  onChange?: () => void;
}

const FIELDS: Array<{ key: keyof Omit<SegmentPersonaInput, 'name'>; label: string; hint: string }> = [
  { key: 'titles', label: 'Titles', hint: 'Directeur général, Responsable qualité' },
  { key: 'seniorities', label: 'Seniorities', hint: 'C-Level, Director, Head' },
  { key: 'departments', label: 'Departments', hint: 'Management, Operations, IT' },
  { key: 'sectors', label: 'Sectors', hint: 'social, santé' },
  { key: 'cantons', label: 'Cantons', hint: 'VD, GE, FR' },
  { key: 'topics', label: 'Topics', hint: 'financement cantonal, protection des données' },
];

const EMPTY = { name: '', titles: '', seniorities: '', departments: '', sectors: '', cantons: '', topics: '' };

/** Surfe's `seniorities` enumeration, spelled its way. Mirrored from
 *  `server/services/personas.ts` because the client may not import server code; the server is
 *  the authority and refuses anything outside it, naming the ten. Offered here as a datalist so
 *  the operator meets the vocabulary while writing the form, not months later on a failed search. */
const SURFE_SENIORITIES = [
  'Board Member', 'C-Level', 'Director', 'Founder', 'Head', 'Manager', 'Other', 'Owner', 'Partner', 'VP',
];

function criteriaOf(persona: Persona): Trait[] {
  return (persona.traits ?? []).filter((t) => t && t.value);
}

export default function SegmentPersonaZone({ onChange }: SegmentPersonaZoneProps) {
  const t = useT();
  const { fmtDate } = useLocale();
  const toast = useToast();

  const [items, setItems] = useState<Persona[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY });
  const [busy, setBusy] = useState<number | 'new' | null>(null);
  const [result, setResult] = useState<LookalikeResult | null>(null);

  const load = useCallback(async () => {
    try {
      const answer = await listPersonas({ subject_kind: 'segment' });
      setItems(answer.items);
      setError(null);
    } catch (err) {
      setItems(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const submit = useCallback(async () => {
    const name = draft.name.trim();
    if (!name) { toast('err', t('A segment persona needs a name.')); return; }
    setBusy('new');
    try {
      const persona = await createSegmentPersona({
        name,
        titles: draft.titles.split(',').map((s) => s.trim()).filter(Boolean),
        seniorities: draft.seniorities.split(',').map((s) => s.trim()).filter(Boolean),
        departments: draft.departments.split(',').map((s) => s.trim()).filter(Boolean),
        sectors: draft.sectors.split(',').map((s) => s.trim()).filter(Boolean),
        cantons: draft.cantons.split(',').map((s) => s.trim()).filter(Boolean),
        topics: draft.topics.split(',').map((s) => s.trim()).filter(Boolean),
      });
      setFormOpen(false);
      setDraft({ ...EMPTY });
      toast('ok', t('{name} held as version {n}.', { name: persona.name ?? name, n: String(persona.version) }));
      await load();
      onChange?.();
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not write the segment persona.'));
    } finally {
      setBusy(null);
    }
  }, [draft, load, onChange, t, toast]);

  const find = useCallback(async (persona: Persona) => {
    setBusy(persona.id);
    try {
      const answer = await lookalikes(persona.id);
      setResult(answer);
      toast(answer.proposed ? 'ok' : 'err', answer.proposed
        ? t('{n} candidates proposed. They wait in Review; none is on the record.', { n: String(answer.proposed) })
        : t('Nothing new: {known} of the answers are already on the record.', { known: String(answer.already_known) }));
      onChange?.();
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('The lookalike search failed.'));
    } finally {
      setBusy(null);
    }
  }, [onChange, t, toast]);

  return (
    <Zone>
      <ZoneHead
        label={t('Segment persona')}
        count={items ? <Fig>{items.length}</Fig> : undefined}
        note={t('who we want to meet · written held, because you are its author')}
        route={
          <button type="button" className="zone-route" onClick={() => setFormOpen((v) => !v)}>
            {formOpen ? t('Close') : t('New segment')} →
          </button>
        }
      />
      <Sheet clip={!!items?.length && !formOpen} pad={!items?.length || formOpen}>
        {formOpen && (
          <div className="flex gap-8 flex-wrap" style={{ alignItems: 'flex-end', marginBottom: 'var(--s12)' }}>
            <div className="form-group" style={{ flex: '1 1 260px' }}>
              <label className="form-label" htmlFor="segment-name">{t('Name')}</label>
              <input
                id="segment-name"
                className="form-input"
                value={draft.name}
                placeholder={t('Directions générales du social vaudois')}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              />
            </div>
            {FIELDS.map((f) => (
              <div key={f.key} className="form-group" style={{ flex: '1 1 200px' }}>
                <label className="form-label" htmlFor={`segment-${f.key}`}>{t(f.label)}</label>
                <input
                  id={`segment-${f.key}`}
                  className="form-input"
                  value={draft[f.key]}
                  placeholder={f.hint}
                  list={f.key === 'seniorities' ? 'segment-seniorities' : undefined}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                />
              </div>
            ))}
            <button type="button" className="btn btn-accent" disabled={busy === 'new'} onClick={() => { void submit(); }}>
              {busy === 'new' ? t('Saving…') : t('Hold this segment')}
            </button>
            <Lnk onClick={() => { setFormOpen(false); setDraft({ ...EMPTY }); }}>{t('Cancel')}</Lnk>
            <datalist id="segment-seniorities">
              {SURFE_SENIORITIES.map((word) => <option key={word} value={word} />)}
            </datalist>
            <p className="rat" style={{ flexBasis: '100%' }}>
              {t('Comma-separated. A field you leave empty is not a criterion. Sectors and cantons choose which organisations of your own record the search is anchored to; topics cannot be searched on Surfe and the answer says so.')}
            </p>
            <p className="rat" style={{ flexBasis: '100%' }}>
              {t('Seniorities are Surfe’s own ten words and nothing else is accepted: {list}. Anything you write is matched to one of them or refused here, so a segment cannot be saved that no search could ever run.', { list: SURFE_SENIORITIES.join(', ') })}
            </p>
          </div>
        )}

        {error ? (
          <p className="zone-empty">{t('The segments could not be read: {error}', { error })}</p>
        ) : items === null ? (
          <p className="zone-empty">{t('loading…')}</p>
        ) : items.length === 0 ? (
          !formOpen && (
            <p className="zone-empty">
              {t('No segment persona yet. A segment persona is your own form — titles, seniorities, departments, sectors, cantons, topics — and is written held on the spot, because you are its author. It is what a sequence addresses and what a lookalike search is built from.')}
            </p>
          )
        ) : (
          items.map((persona) => (
            <div key={persona.id} className="row" style={{ gridTemplateColumns: '1fr 200px' }}>
              <span>
                <span className="w">{persona.name}</span>
                <span className="d">
                  <Sep />{t('version')} <Fig>{persona.version}</Fig>
                  <Sep /><Fig>{fmtDate(persona.created_at)}</Fig>
                  {persona.status === 'held' && <> <Att kind="held" /></>}
                </span>
                <br />
                {criteriaOf(persona).map((trait) => (
                  <Tag key={String(trait.key)} tone="ghost">
                    {t(String(trait.key))}: {trait.value}
                  </Tag>
                ))}
              </span>
              <span className="flex gap-8">
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={busy === persona.id}
                  title={t('Searches Surfe for people like this segment and files each as a candidate proposal')}
                  onClick={() => { void find(persona); }}
                >
                  {busy === persona.id ? t('Searching…') : t('Find lookalikes')}
                </button>
              </span>
            </div>
          ))
        )}

        {result && (
          <div className="row" style={{ gridTemplateColumns: '1fr auto' }}>
            <span className="d">
              {t('{proposed} proposed of {requested} asked', { proposed: String(result.proposed), requested: String(result.requested) })}
              <Sep />
              {t('{known} already on the record', { known: String(result.already_known) })}
              <Sep />
              {result.capped
                ? <Tag tone="attend">{t('{used} of {cap} today', { used: String(result.used_today), cap: String(result.cap) })}</Tag>
                : <>{t('{used} of {cap} today', { used: String(result.used_today), cap: String(result.cap) })}</>}
              {result.unused_criteria.length > 0 && (
                <>
                  <Sep />
                  {t('not searchable on Surfe: {list}', { list: result.unused_criteria.join(', ') })}
                </>
              )}
              {result.snapshots_requested > 0 && (
                <>
                  <Sep />
                  {t('{n} company pages asked of the scout', { n: String(result.snapshots_requested) })}
                </>
              )}
            </span>
            <span>
              <button type="button" className="zone-route" onClick={() => open(ROUTES.review({ kind: 'candidate' }))}>
                {t('Review the candidates')} →
              </button>
            </span>
          </div>
        )}
      </Sheet>
    </Zone>
  );
}
